import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Reusable Barcode/QR Scanner Component
 * Uses html5-qrcode library for camera-based scanning
 * 
 * Props:
 *   onScan(decodedText, decodedResult) - called on successful scan
 *   onError(errorMessage) - called on scan error (optional)
 *   onClose() - called when scanner is closed
 *   scanMode - 'camera' | 'image' (default: 'camera')
 *   facingMode - 'environment' | 'user' (default: 'environment' = back camera)
 *   fps - frames per second (default: 10)
 *   qrboxSize - scanning box size in px (default: 250)
 *   showSwitch - show camera switch button (default: true)
 *   continuous - keep scanning after first result (default: false)
 *   title - header title (default: 'Scan Barcode / QR Code')
 */
export default function BarcodeScanner({
  onScan,
  onError,
  onClose,
  scanMode = 'camera',
  facingMode = 'environment',
  fps = 10,
  qrboxSize = 250,
  showSwitch = true,
  continuous = false,
  title = 'Scan Barcode / QR Code',
}) {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [lastResult, setLastResult] = useState('');
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [activeCamera, setActiveCamera] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize scanner
  const startScanner = useCallback(async (cameraId) => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      
      // Stop existing scanner if any
      if (html5QrCodeRef.current) {
        try { await html5QrCodeRef.current.stop(); } catch {}
        html5QrCodeRef.current.clear();
      }

      const scanner = new Html5Qrcode('barcode-scanner-region');
      html5QrCodeRef.current = scanner;

      const config = {
        fps,
        qrbox: { width: qrboxSize, height: Math.floor(qrboxSize * 0.6) },
        aspectRatio: 1.5,
        disableFlip: false,
        // Support all common barcode formats
        formatsToSupport: undefined, // undefined = all formats
      };

      const onSuccess = (decodedText, decodedResult) => {
        // Debounce: don't fire same result within 2 seconds
        if (decodedText === lastResult) return;
        setLastResult(decodedText);

        // Vibrate on mobile for feedback
        if (navigator.vibrate) navigator.vibrate(100);

        onScan(decodedText, decodedResult);

        if (!continuous) {
          stopScanner();
        }

        // Reset debounce after 2 seconds
        setTimeout(() => setLastResult(''), 2000);
      };

      const onFailure = () => {
        // Scanning frame didn't find a code — this is normal, ignore
      };

      if (cameraId) {
        await scanner.start(cameraId, config, onSuccess, onFailure);
      } else {
        await scanner.start(
          { facingMode },
          config,
          onSuccess,
          onFailure
        );
      }

      setIsScanning(true);
      setError('');
    } catch (err) {
      const msg = err?.message || 'Camera access denied or unavailable';
      setError(msg);
      if (onError) onError(msg);
    }
  }, [facingMode, fps, qrboxSize, continuous, onScan, onError, lastResult]);

  // Stop scanner
  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
      } catch {}
      try { html5QrCodeRef.current.clear(); } catch {}
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  }, []);

  // Get available cameras
  useEffect(() => {
    const getCameras = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const devices = await Html5Qrcode.getCameras();
        setCameras(devices);
        if (devices.length > 0) {
          // Prefer back camera
          const backCam = devices.find(d => 
            d.label.toLowerCase().includes('back') || 
            d.label.toLowerCase().includes('rear') ||
            d.label.toLowerCase().includes('environment')
          );
          setActiveCamera(backCam?.id || devices[0].id);
        }
      } catch {
        setError('Unable to access cameras. Please grant camera permission.');
      }
    };
    getCameras();
    return () => { stopScanner(); };
  }, [stopScanner]);

  // Start scanning when camera is selected
  useEffect(() => {
    if (activeCamera && scanMode === 'camera') {
      startScanner(activeCamera);
    }
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCamera]);

  // Switch camera
  const switchCamera = async () => {
    if (cameras.length < 2) return;
    const currentIdx = cameras.findIndex(c => c.id === activeCamera);
    const nextIdx = (currentIdx + 1) % cameras.length;
    setActiveCamera(cameras[nextIdx].id);
  };

  // Toggle torch/flash
  const toggleTorch = async () => {
    if (!html5QrCodeRef.current) return;
    try {
      const track = html5QrCodeRef.current.getRunningTrackSettings?.();
      if (track) {
        await html5QrCodeRef.current.applyVideoConstraints({
          advanced: [{ torch: !torchOn }]
        });
        setTorchOn(!torchOn);
      }
    } catch {
      // Torch not supported on this device
    }
  };

  // Scan from image file
  const handleFileScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      // Stop camera if running
      await stopScanner();

      const scanner = new Html5Qrcode('barcode-scanner-region');
      html5QrCodeRef.current = scanner;

      const result = await scanner.scanFile(file, true);
      onScan(result, { format: 'file' });
      scanner.clear();
    } catch (err) {
      setError('No barcode found in image');
      if (onError) onError('No barcode found in image');
    }
  };

  // Handle close
  const handleClose = () => {
    stopScanner();
    if (onClose) onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="modal modal-md" style={{ overflow: 'hidden' }}>
        {/* Header */}
        <div className="modal-header">
          <h2>{title}</h2>
          <button onClick={handleClose} className="btn btn-icon" style={{ fontSize: 18 }}>✕</button>
        </div>

        {/* Scanner Body */}
        <div style={{ background: '#000', position: 'relative' }}>
          {/* Scanner region — html5-qrcode renders here */}
          <div 
            id="barcode-scanner-region" 
            ref={scannerRef}
            style={{ width: '100%', minHeight: 300 }}
          />

          {/* Overlay controls */}
          {isScanning && (
            <div style={{
              position: 'absolute', bottom: 12, left: 0, right: 0,
              display: 'flex', justifyContent: 'center', gap: 12, zIndex: 10,
            }}>
              {showSwitch && cameras.length > 1 && (
                <button onClick={switchCamera} style={overlayBtnStyle} title="Switch Camera">
                  🔄
                </button>
              )}
              <button onClick={toggleTorch} style={{
                ...overlayBtnStyle,
                background: torchOn ? 'rgba(255,200,0,0.8)' : 'rgba(255,255,255,0.2)',
              }} title="Toggle Flash">
                💡
              </button>
            </div>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div style={{ padding: '10px 16px', background: '#f8d7da', color: '#842029', fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Footer controls */}
        <div className="modal-body" style={{ padding: '12px 16px' }}>
          <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
            {/* Scan from image */}
            <input 
              type="file" 
              ref={fileInputRef}
              accept="image/*" 
              onChange={handleFileScan}
              style={{ display: 'none' }}
            />
            <button 
              onClick={() => fileInputRef.current?.click()} 
              className="btn btn-outline btn-sm"
            >
              📷 Scan from Image
            </button>

            {/* Manual entry fallback */}
            <div style={{ flex: 1 }} />

            <ManualEntryInput onSubmit={(code) => onScan(code, { format: 'manual' })} />
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-light)' }}>
            Supports: EAN-13, EAN-8, UPC-A, UPC-E, Code-128, Code-39, ITF, QR Code, Data Matrix, PDF-417
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Manual entry sub-component ──────────────────────────────
function ManualEntryInput({ onSubmit }) {
  const [code, setCode] = useState('');
  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.trim()) {
      onSubmit(code.trim());
      setCode('');
    }
  };
  return (
    <form onSubmit={handleSubmit} className="flex gap-2 items-center">
      <input 
        className="form-input" 
        style={{ width: 160, padding: '5px 10px', fontSize: 13 }}
        placeholder="Enter code manually" 
        value={code} 
        onChange={e => setCode(e.target.value)} 
      />
      <button type="submit" className="btn btn-primary btn-sm">Go</button>
    </form>
  );
}

// ─── Style helper ────────────────────────────────────────────
const overlayBtnStyle = {
  width: 44, height: 44, borderRadius: '50%',
  background: 'rgba(255,255,255,0.2)', border: 'none',
  color: '#fff', fontSize: 20, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backdropFilter: 'blur(4px)',
};
